/**
 * Utility functions for handling filter visibility and messages based on content counts
 */

interface FilterState {
  totalCount: number
  totalUnfilteredCount: number
  isLoading: boolean
  hasActiveFilters: boolean
  contentType?: 'videos' | 'images' | 'transcriptions'
}

interface FilterDisplayConfig {
  shouldShowFilters: boolean
  shouldShowNoContentMessage: boolean
  shouldShowNoFilteredResultsMessage: boolean
  message: string
}

/**
 * Determines what UI elements to show based on content counts and filter state
 */
export const getFilterDisplayConfig = ({
  totalCount,
  totalUnfilteredCount,
  isLoading,
  hasActiveFilters,
  contentType = 'videos'
}: FilterState): FilterDisplayConfig => {


  // Don't show anything while loading
  if (isLoading) {
    return {
      shouldShowFilters: false,
      shouldShowNoContentMessage: false,
      shouldShowNoFilteredResultsMessage: false,
      message: "",
    }
  }

  // No content at all (regardless of filters)
  // IMPORTANT: Check both totalCount and totalUnfilteredCount to handle edge cases
  // where data exists but isn't being shown due to query/filter issues
  if (totalUnfilteredCount === 0 && totalCount === 0) {
    const contentMessages = {
      videos: "No videos available yet. Start creating!",
      images: "No images available yet. Start creating!",
      transcriptions: "No transcriptions available yet. Start creating!"
    };
    
    return {
      shouldShowFilters: false, // Hide filters when there's no content
      shouldShowNoContentMessage: true,
      shouldShowNoFilteredResultsMessage: false,
      message: contentMessages[contentType],
    }
  }

  // Has content but current filters return no results
  if (totalCount === 0 && hasActiveFilters) {
    const contentMessages = {
      videos: `No videos match your current filters. Try changing the status filter or sorting options. (${totalUnfilteredCount} total videos available)`,
      images: `No images match your current filters. Try changing the status filter or sorting options. (${totalUnfilteredCount} total images available)`,
      transcriptions: `No transcriptions match your current filters. Try changing the status filter or sorting options. (${totalUnfilteredCount} total transcriptions available)`
    };
    
    return {
      shouldShowFilters: true, // Show filters so user can adjust them
      shouldShowNoContentMessage: false,
      shouldShowNoFilteredResultsMessage: true,
      message: contentMessages[contentType],
    }
  }

  // Normal case: has content and filters may or may not be applied
  return {
    shouldShowFilters: true,
    shouldShowNoContentMessage: false,
    shouldShowNoFilteredResultsMessage: false,
    message: "",
  }
}

/**
 * Helper to determine if there are active filters
 * You can extend this based on your specific filter criteria
 */
export const hasActiveFilters = (filters: {
  status?: string
  sort_by?: string
  search?: string
  [key: string]: any
}): boolean => {
  
  // Define what constitutes "default" or "no filter" values
  const defaultValues = {
    status: ['all'], // Only "all" means no specific filter applied
    sort_by: ['recent', 'oldest'], // These are just sorting, not filtering
  };
  
  const hasActiveFilter = Object.entries(filters).some(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return false;
    }
    
    // For status and sort_by, check against their default values
    if (key === 'status') {
      return !defaultValues.status.includes(value as string);
    }
    if (key === 'sort_by') {
      return false; // Sort by is not considered a "filter" for our purposes
    }
    
    // For other fields, any non-empty value is considered active
    return value !== 'all';
  });
  
  return hasActiveFilter;
}

/**
 * Get appropriate no results message based on filter state
 */
export const getNoResultsMessage = (
  contentType: "videos" | "images" | "transcriptions",
  totalUnfilteredCount: number,
  hasFilters: boolean
): string => {
  if (totalUnfilteredCount === 0) {
    const actionMap = {
      videos: "upload or generate",
      images: "generate",
      transcriptions: "create",
    }
    return `No ${contentType} available yet. Start by ${actionMap[contentType]} your first ${contentType.slice(0, -1)}!`
  }

  if (hasFilters) {
    return `No ${contentType} match your current filters. Try adjusting your search criteria or clearing filters. (${totalUnfilteredCount} total ${contentType} available)`
  }

  return `No ${contentType} found.`
}